import { Link } from "react-router";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import { useWindowSize } from "@hooks/useWindowSize";
import { PodcastKeywordData } from "@pages/Section/layouts/PodcastsPage/utils/types";
import { cn } from "@util/helpers";

type PodcastsKeywordsDetailsCardProps = {
  /** Details about the podcast keyword */
  details: PodcastKeywordData;
};

/**
 * Podcasts Keywords Details Card Component
 */
const PodcastsKeywordsDetailsCard = ({
  details: { label, description, thumbnailSrc, tag, byline, subscribe },
}: PodcastsKeywordsDetailsCardProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();

  return (
    <div
      data-testid="podcasts-keywords-details-card-component"
      className="font-poppins"
    >
      <div className="mb-6 flex">
        {isScreenLG ? (
          <img
            src={thumbnailSrc}
            alt={label}
            width={140}
            height={140}
            className="h-full"
          />
        ) : null}

        <div className="flex flex-wrap pl-0 lg:flex-col lg:flex-nowrap lg:pl-6">
          <div className="mb-4 flex items-center lg:mb-1">
            <Link
              to={tag.urlPath}
              className="text-2xs font-semibold lg:text-base"
            >
              {label}
            </Link>

            <IconFactory type={IconTypeEnum.rightCaret} className="inline" />
          </div>

          <p
            className={cn(
              "mb-0 basis-[calc(100%-52px-1rem)] font-poppins text-2xs text-gray-650 lg:basis-full lg:text-sm",
              { "basis-full": !byline || byline.length > 1 }
            )}
          >
            {description}
          </p>

          {byline ? (
            <div
              className={cn(
                "-mt-[2px] ml-auto flex items-start gap-2 lg:-mb-[2px] lg:ml-0 lg:mt-auto",
                { "ml-0 pt-3": byline.length > 1 }
              )}
            >
              {byline.map((individualByline, index) => (
                <img
                  key={`podcast-details-byline-${index}`}
                  className={cn(
                    "rounded-full border-[2px] border-white bg-gray-250",
                    { "-ml-4": index !== 0 }
                  )}
                  src={individualByline}
                  width={52}
                  height={52}
                  alt="byline"
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {subscribe ? (
        <div className="mb-6 flex flex-wrap gap-2 font-poppins text-2xs text-gray-850 lg:text-sm">
          <p className="mb-0 basis-full text-gray-650 lg:basis-auto">
            Subscribe:
          </p>

          <div>
            {subscribe.map(({ platform, link }) => (
              <Link
                key={`podcast-details-subscribe-${platform}`}
                to={link}
                target="_blank"
                className={cn(
                  "relative mx-2 inline-block font-medium first:ml-0 first:mr-2 hover:underline ",
                  "before:absolute before:-left-2 before:h-full before:w-[1px] before:bg-gray-175 first:before:hidden"
                )}
                rel="noreferrer"
                aria-label={platform}
              >
                {platform}
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PodcastsKeywordsDetailsCard;
